import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAdminAuth } from "./AuthContext";
import { LogIn } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.email) return <Navigate to="/admin" replace />;

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#4A0E1F] px-6 py-24 text-[#FDFBF7]">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-md"
      >
        <div className="mb-3 flex items-center gap-3">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
            The Atelier · Admin
          </span>
        </div>
        <h1 className="font-serif text-5xl leading-none tracking-tight">
          Sign in
          <span className="italic text-[#D4AF37]"> to the studio.</span>
        </h1>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="mt-12 space-y-8"
        >
          <label className="block">
            <span className="line-label">Email</span>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="line-input"
              placeholder="admin@fairylights.ae"
              data-testid="admin-email"
            />
          </label>
          <label className="block">
            <span className="line-label">Password</span>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="line-input"
              placeholder="••••••••••••"
              data-testid="admin-password"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="pill pill-primary disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Enter the atelier"}
            <LogIn className="h-4 w-4" />
          </button>
        </form>

        <a
          href="/"
          className="mt-16 inline-block font-body text-[11px] uppercase tracking-[0.24em] text-[#FDFBF7]/50 hover:text-[#D4AF37] transition-colors"
        >
          ← Back to the public profile
        </a>
      </motion.div>
    </div>
  );
}
